import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { runThirdParty } from '../../helpers/thirdparty'
import { useFormStartWhenVisible } from '../../helpers/formAnalytics'
import { registerPrefillHandler, applyPrefillToForm, registerRefreshOnSubmit } from './d365FormPrefill'
import FormSubmitOverlay from '../helpers/FormSubmitOverlay'

/** Sibling of the member-context "current" route that re-syncs the cache from D365. */
function toRefreshUrl(memberContextUrl) {
  return memberContextUrl ? memberContextUrl.replace(/\/current$/, '/refresh') : null
}

// Backstop reload window. registerRefreshOnSubmit reloads the page as soon as D365
// reflects the member's edit (polling with backoff), but if every poll attempt fails
// it never reloads — which would strand the member under the "Saving your changes…"
// placeholder. This must sit just past the poll schedule (~127.5s of delays plus a
// few seconds of /refresh round-trips per attempt) so it only fires when the normal
// reload never came; the happy-path reload unloads the page first.
const SAFETY_RELOAD_MS = 150000

/**
 * Stacks multiple D365 Marketing embedded forms on one page (member/staff self-service QA).
 * When {@link memberContextUrl} is set, loads the signed-in member's D365 contact (and related
 * ids) and passes them as `data-prefill-*` on each embed — same pattern as RegistrationForm —
 * so Marketing can identify the contact and expose editable fields per the form design.
 */
function MemberSelfServiceForms({ formApiUrl, forms, memberContextUrl, isSectionVisible = true }) {
  const rootRef = useRef(null)
  const [memberContext, setMemberContext] = useState(null)
  const [contextReady, setContextReady] = useState(false)
  // formId → true while that form shows the post-submit "Saving your changes…"
  // placeholder, covering D365's "form submitted" view until the reload lands.
  const [savingFormIds, setSavingFormIds] = useState(() => new Set())
  const safetyReloadRef = useRef(null)
  const dlFormType = 'member_self_service'

  useFormStartWhenVisible(dlFormType, { ready: contextReady, isSectionVisible })

  // Cover the submitted form the instant it is submitted (before the poll + reload
  // in registerRefreshOnSubmit), scoped to its [data-form-id] wrapper so stacked
  // forms are unaffected. No hide step: the reload clears everything; the safety
  // timeout guarantees a reload even if every refresh poll fails.
  const handleSubmitStart = useCallback((container) => {
    const formId = container?.dataset?.formId
    if (!formId) return
    setSavingFormIds((prev) => new Set(prev).add(formId))
    // Scroll to the top so the "Saving your changes…" overlay is in view rather than the
    // now-covered form the member was editing further down the page.
    window.scrollTo({ top: 0, behavior: 'smooth' })
    window.clearTimeout(safetyReloadRef.current)
    safetyReloadRef.current = window.setTimeout(() => window.location.reload(), SAFETY_RELOAD_MS)
  }, [])

  useEffect(() => () => window.clearTimeout(safetyReloadRef.current), [])

  useEffect(() => {
    if (!memberContextUrl) {
      setContextReady(true)
      return
    }
    let cancelled = false
    fetch(memberContextUrl, { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) setMemberContext(data)
      })
      .catch((err) => {
        console.error('[MemberSelfServiceForms] Failed to load member context:', err)
        if (!cancelled) setMemberContext(null)
      })
      .finally(() => {
        if (!cancelled) setContextReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [memberContextUrl])

  useEffect(() => {
    if (!contextReady || !rootRef.current) return
    const nodes = rootRef.current.querySelectorAll('[data-form-id]')
    nodes.forEach((el) => delete el.dataset.processed)

    // Register the prefill listener BEFORE FormLoader.bundle.js fires
    // d365mkt-afterformload. If the bundle is already loaded the listener
    // attaches now; otherwise registerPrefillHandler is a no-op until the
    // global appears and we re-register inside the retry loop below.
    registerPrefillHandler(memberContext)

    // After a member edits + submits a form, re-sync their Umbraco cache from D365
    // (polling with backoff so we capture D365's async write, not the pre-edit
    // snapshot), then reload the page. A full reload is required because D365 RTM
    // forms cannot re-render in place — their inline scripts re-declare top-level
    // identifiers on a second render (FormRender.reinjectScripts throws
    // "Identifier 'formContainer' has already been declared"). Reloading brings the
    // form back fresh and prefilled from the now-updated /current snapshot, instead
    // of leaving the member on D365's "thank you" view with stale data.
    const refreshUrl = toRefreshUrl(memberContextUrl)
    const onRefreshed = () => window.location.reload()
    registerRefreshOnSubmit(refreshUrl, onRefreshed, memberContext, handleSubmitStart)

    runThirdParty()

    // FormLoader.bundle.js is async; on cached loads the form can render
    // before our `d365mkt-afterformload` listener attaches. Re-register
    // (idempotent — replaces the prior handler) and apply prefill manually
    // for any forms already in the DOM. Bounded retry guards against the
    // bundle never loading (e.g. on local dev without network).
    let attempts = 0
    const retryId = window.setInterval(() => {
      attempts += 1
      registerPrefillHandler(memberContext)
      registerRefreshOnSubmit(refreshUrl, onRefreshed, memberContext, handleSubmitStart)
      rootRef.current?.querySelectorAll('form').forEach((form) => {
        applyPrefillToForm(form, memberContext)
      })
      if (attempts >= 10) window.clearInterval(retryId)
    }, 500)

    return () => window.clearInterval(retryId)
  }, [formApiUrl, forms, contextReady, memberContext, memberContextUrl, handleSubmitStart])

  if (!formApiUrl || !Array.isArray(forms) || forms.length === 0) return null

  // NOTE: `data-prefill-*` attributes below are a no-op. Microchannel's
  // FormLoader.bundle.js does NOT read these — pre-fill happens via the
  // `d365mkt-afterformload` event handler registered above (see
  // d365FormPrefill.js). Left in place for parity with RegistrationForm.jsx
  // until that component is migrated to the same mechanism.
  const prefillAttrs = buildPrefillAttributes(memberContext)

  return (
    <div ref={rootRef} className="member-self-service-forms space-y-12">
      {forms.map((item) => {
        if (!item?.formId) return null
        const saving = savingFormIds.has(item.formId)
        return (
          <section
            key={item.formId}
            className="member-self-service-forms__section"
            aria-labelledby={item.title ? `d365-form-heading-${item.formId}` : undefined}
          >
            {item.title ? (
              <h2 id={`d365-form-heading-${item.formId}`} className="text-midnight-500 mb-6 text-2xl font-semibold">
                {item.title}
              </h2>
            ) : null}
            <div
              className={`member-self-service-form-embed relative ${saving ? 'min-h-[600px]' : ''}`}
              aria-busy={saving}
            >
              {/* On submit, hide the embed and show the ~600px spinner cover in its place so the
                  loading spinner is clearly visible (never clipped by the embed). The form returns
                  fresh when the post-submit refresh reloads the page. */}
              <div
                className={saving ? 'hidden' : undefined}
                data-form-id={item.formId}
                data-form-api-url={formApiUrl}
                data-cached-form-url={item.cachedFormUrl}
                {...prefillAttrs}
              />
              {saving ? (
                <FormSubmitOverlay className="rounded-3xl rounded-tl-none" message="Saving your changes…" />
              ) : null}
            </div>
          </section>
        )
      })}
    </div>
  )
}

/** Mirrors RegistrationForm prefill attribute names for D365 FormLoader. */
function buildPrefillAttributes(ctx) {
  if (!ctx?.contactId) return {}
  const attrs = {}
  const add = (value, attr) => {
    if (value) attrs[attr] = value
  }
  add(ctx.contactId, 'data-prefill-contact-id')
  add(ctx.providerId, 'data-prefill-provider-id')
  add(ctx.firstName, 'data-prefill-first-name')
  add(ctx.lastName, 'data-prefill-last-name')
  add(ctx.email, 'data-prefill-email')
  return attrs
}

MemberSelfServiceForms.propTypes = {
  formApiUrl: PropTypes.string,
  memberContextUrl: PropTypes.string,
  isSectionVisible: PropTypes.bool,
  forms: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      formId: PropTypes.string,
      cachedFormUrl: PropTypes.string,
    }),
  ),
}

export default MemberSelfServiceForms
